import { FC, Key, ReactNode } from 'react'
import PdMedia from '../pd-media/pd-media'
import { TitleBarTheme } from './title-bar'
import clsx from 'clsx'
import useScrollScale from '@hooks/tools/use-scroll-scale'
import { HeadingTag, SemanticTextTag } from '../../common/pd-meta-components-types'
import SemanticText from '../standard/semantic-text'

type TitleMediaProps = {
  title: string[]
  titleTag?: HeadingTag
  desc: Array<string | ReactNode>
  descTags?: SemanticTextTag[]
  extraNode?: ReactNode
  mediaUri: string
  mediaClassName?: string
  mediaStyle?: { [key in string]: Key }
  rowMediaList?: { mediaUri: string; mediaClassName?: string; mediaStyle?: { [key in string]: Key } }[]
  rowMediaDesc?: string[]
  rowMediaDescTags?: SemanticTextTag[]
  theme?: TitleBarTheme
  titleClassName?: string
  descClassName?: string
  enableScrollScale?: boolean
}
const TitleMedia: FC<TitleMediaProps> = ({
  theme = TitleBarTheme.black,
  title,
  titleTag,
  desc,
  descTags,
  mediaUri,
  mediaStyle,
  extraNode,
  rowMediaList,
  rowMediaDesc,
  rowMediaDescTags,
  mediaClassName,
  titleClassName,
  descClassName,
  enableScrollScale = false,
}) => {
  const containerRef = useScrollScale({
    targetSelector: '.secure-target',
    enableScrollScale,
  })

  return (
    <div
      className={[
        theme === TitleBarTheme.black ? 'bg-black' : 'bg-[#f2f3f5]',
        'w-full h-fit ',
        rowMediaList?.length ? 'pt-15' : 'py-15',
        'laptop:py-30',
      ].join(' ')}
    >
      <div className={['w-full mx-auto', 'laptop:w-300'].join(' ')}>
        {/* title */}
        <div className={['mb-3 px-6', extraNode && 'mb-8', 'laptop:px-0 laptop:mb-6'].join(' ')}>
          {title.map((item, index) => (
            <SemanticText
              key={index}
              as={titleTag}
              defaultTag="h3"
              className={[
                theme === TitleBarTheme.black ? 'text-white/90' : 'text-black/90',
                'text-24 font-semibold',
                'laptop:text-center laptop:text-48',
                titleClassName,
              ].join(' ')}
            >
              {item}
            </SemanticText>
          ))}
        </div>
        {/* desc */}
        <div
          className={[
            'mb-8 px-6 flex flex-col',
            'laptop:flex-row laptop:px-0 laptop:mb-64px laptop:relative',
            extraNode ? 'laptop:justify-between' : 'laptop:justify-center',
          ].join(' ')}
        >
          <div className={['hidden laptop:inline-block'].join(' ')}>{extraNode}</div>
          <div className={['w-full', 'laptop:w-200'].join(' ')}>
            {desc.map((item, index) => (
              <SemanticText
                key={index}
                as={descTags?.[index]}
                defaultTag="p"
                className={[
                  theme === TitleBarTheme.black ? 'text-white/90' : 'text-black/90',
                  'text-16',
                  'laptop:text-18',
                  !extraNode && 'laptop:text-center',
                  descClassName,
                ].join(' ')}
              >
                {item}
              </SemanticText>
            ))}
          </div>
          <div className={['inline-block laptop:hidden', extraNode && 'pt-3'].join(' ')}>{extraNode}</div>
        </div>
        {/* media */}
        <div style={{ ...mediaStyle }} className={['w-full ', mediaClassName].join(' ')} ref={containerRef}>
          <PdMedia
            src={mediaUri}
            mask={false}
            imageClassName={clsx(enableScrollScale && 'secure-target scale-[1.3]')}
          />
        </div>
        {/* row media desc*/}
        <div className={['w-full mt-8 laptop:mt-64px'].join(' ')}>
          {rowMediaDesc && (
            <div
              className={[
                'px-6 flex flex-col',
                'laptop:flex-row laptop:px-0 laptop:relative',
                'laptop:justify-center',
              ].join(' ')}
            >
              <div className={['w-full', 'laptop:w-200 '].join(' ')}>
                {rowMediaDesc?.map((item, index) => (
                  <SemanticText
                    key={index}
                    as={rowMediaDescTags?.[index]}
                    defaultTag="p"
                    className={[
                      theme === TitleBarTheme.black ? 'text-white/90' : 'text-black/90',
                      ' text-16',
                      'laptop:text-18',
                      'laptop:text-center',
                    ].join(' ')}
                  >
                    {item}
                  </SemanticText>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* row media */}
        <div
          className={[
            'flex flex-col mt-8 gap-8',
            'laptop:mt-64px laptop:flex-row laptop:items-center laptop:justify-between laptop:flex-wrap laptop:gap-0',
          ].join(' ')}
        >
          {rowMediaList?.map((item, index) => (
            <div key={index} style={{ ...item?.mediaStyle }} className={['w-full ', item?.mediaClassName].join(' ')}>
              <PdMedia src={item.mediaUri} mask={false} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default TitleMedia
